import React, {useState} from "react";
import {Box, Button, Typography, Paper, Alert, CircularProgress, Link} from "@mui/material";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {forgotPassword} from "../../api/auth";
import LanguageSelector from "../../components/common/LanguageSelector";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";

const ForgotPasswordPage: React.FC = () => {
  const {t} = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      email: "",
    },
    validationConfig: FORM_VALIDATION_CONFIGS.FORGOT_PASSWORD,
    onSubmit: async (values) => {
      try {
        const response = await forgotPassword(values.email);
        setSuccess(response.message);
        form.reset(); // Clear form
      } catch (err: any) {
        console.error("Forgot password error:", err);
        setError(err.response?.data?.message || "Failed to send reset email. Please try again.");
        throw err;
      }
    },
    onError: (errors) => {
      console.log("Validation errors:", errors);
    },
  });

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f5f6fa"
      sx={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: 400,
          borderRadius: 2,
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
        }}>
        {/* Language Selector */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <LanguageSelector />
        </Box>

        <Box textAlign="center" mb={3}>
          <Typography variant="h5" component="h1" fontWeight="bold" color="primary">
            SmartFarm
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Forgot Password
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          Enter your email address and we'll send you a link to reset your password.
        </Typography>

        <form onSubmit={form.handleSubmit}>
          <FormField
            name="email"
            label={t("Email")}
            type="email"
            value={form.values.email}
            onChange={form.handleChange("email")}
            error={form.errors.email}
            required
            autoComplete="email"
            autoFocus
            disabled={form.isSubmitting}
          />

          {error && (
            <Alert severity="error" sx={{mt: 2, mb: 1}}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{mt: 2, mb: 1}}>
              {success}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 1,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
            }}
            type="submit"
            disabled={form.isSubmitting}>
            {form.isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Send Reset Link"}
          </Button>

          <Box textAlign="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              Remember your password?{" "}
              <Link component={RouterLink} to="/login" color="primary">
                Sign in
              </Link>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ForgotPasswordPage;
